import React, { useState } from 'react'
import { FaBookOpen, FaSearch, FaCheckCircle } from 'react-icons/fa'

function VocabularySection() {
    const [searchTerm, setSearchTerm] = useState('')
    const [selectedCategory, setSelectedCategory] = useState('all')

    const categories = [
        { id: 'all', name: 'Todas', color: 'from-gray-600 to-gray-700' },
        { id: 'daily', name: 'Dia a Dia', color: 'from-blue-500 to-cyan-500' },
        { id: 'work', name: 'Trabalho', color: 'from-purple-600 to-indigo-600' },
        { id: 'travel', name: 'Viagem', color: 'from-green-500 to-emerald-600' },
        { id: 'food', name: 'Comida', color: 'from-orange-500 to-red-500' },
    ]

    // Sample vocabulary data
    const vocabularyLists = [
        {
            id: 1,
            title: 'Cumprimentos Básicos',
            category: 'daily',
            words: 15,
            level: 'Intro',
            description: 'Aprenda os cumprimentos essenciais em inglês'
        },
        {
            id: 2,
            title: 'Vocabulário de Escritório',
            category: 'work',
            words: 25,
            level: 'Nível 1',
            description: 'Palavras importantes para o ambiente de trabalho'
        },
        {
            id: 3,
            title: 'Viagem e Turismo',
            category: 'travel',
            words: 30,
            level: 'Nível 2',
            description: 'Vocabulário útil para suas viagens'
        },
        {
            id: 4,
            title: 'Comida e Restaurantes',
            category: 'food',
            words: 20,
            level: 'Nível 1',
            description: 'Termos essenciais para pedir comida'
        },
    ]

    const filteredLists = vocabularyLists.filter(list => {
        const matchesSearch = list.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                            list.description.toLowerCase().includes(searchTerm.toLowerCase())
        const matchesCategory = selectedCategory === 'all' || list.category === selectedCategory
        return matchesSearch && matchesCategory
    })

    const getCategoryGradient = (categoryId) => {
        const category = categories.find(cat => cat.id === categoryId)
        return category ? category.color : 'from-gray-600 to-gray-700'
    }

    return (
        <div className="w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            {/* Header */}
            <div className="text-center mb-8">
                <div className="flex items-center justify-center gap-3 mb-4">
                    <FaBookOpen className="text-3xl text-purple-600" />
                    <h2 className="text-3xl md:text-4xl font-bold text-gray-900">
                        Vocabulário
                    </h2>
                </div>
                <p className="text-lg text-gray-600 max-w-2xl mx-auto">
                    Explore listas de vocabulário organizadas por categoria e nível
                </p>
            </div>

            {/* Search and Filter */}
            <div className="mb-8 space-y-4">
                <div className="relative max-w-md mx-auto">
                    <FaSearch className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400" />
                    <input
                        type="text"
                        placeholder="Buscar vocabulário..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="w-full pl-12 pr-4 py-3 rounded-xl border-2 border-gray-200 focus:border-purple-500 focus:outline-none shadow-md"
                    />
                </div>

                <div className="flex flex-wrap justify-center gap-3">
                    {categories.map(category => (
                        <button
                            key={category.id}
                            onClick={() => setSelectedCategory(category.id)}
                            className={`px-6 py-3 rounded-xl font-semibold transition-all duration-300 ${
                                selectedCategory === category.id
                                    ? `bg-gradient-to-r ${category.color} text-white shadow-lg scale-105`
                                    : 'bg-white text-gray-700 hover:bg-gray-100 shadow-md'
                            }`}
                        >
                            {category.name}
                        </button>
                    ))}
                </div>
            </div>

            {/* Vocabulary Lists Grid */}
            {filteredLists.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {filteredLists.map(list => (
                        <div
                            key={list.id}
                            className="bg-white/80 backdrop-blur-sm rounded-xl p-6 shadow-lg border-2 border-purple-100 hover:shadow-xl hover:border-purple-200 transition-all duration-300 cursor-pointer group"
                        >
                            <div className={`w-full h-2 bg-gradient-to-r ${getCategoryGradient(list.category)} rounded-t-xl -mt-6 -mx-6 mb-4`}></div>
                            <div className="flex items-start justify-between mb-3">
                                <h3 className="text-xl font-bold text-gray-900 group-hover:text-purple-600 transition-colors">
                                    {list.title}
                                </h3>
                                <span className={`px-3 py-1 rounded-full text-xs font-semibold bg-gradient-to-r ${getCategoryGradient(list.category)} text-white`}>
                                    {list.level}
                                </span>
                            </div>
                            <p className="text-gray-600 mb-4 text-sm">
                                {list.description}
                            </p>
                            <div className="flex items-center justify-between pt-4 border-t border-gray-200">
                                <div className="flex items-center gap-2 text-gray-500 text-sm">
                                    <FaBookOpen />
                                    <span>{list.words} palavras</span>
                                </div>
                                <button className="px-4 py-2 bg-gradient-to-r from-purple-600 to-indigo-600 text-white rounded-lg font-semibold hover:from-purple-700 hover:to-indigo-700 transition-all duration-300 text-sm">
                                    Estudar →
                                </button>
                            </div>
                        </div>
                    ))}
                </div>
            ) : (
                <div className="text-center py-12 bg-white/70 backdrop-blur-sm rounded-xl shadow-lg border border-white/50">
                    <FaSearch className="text-5xl text-gray-400 mx-auto mb-4" />
                    <p className="text-xl text-gray-600">Nenhum resultado encontrado</p>
                </div>
            )}

            {/* Coming Soon */}
            <div className="mt-12 bg-gradient-to-r from-purple-50 to-indigo-50 rounded-xl p-8 text-center border-2 border-purple-200">
                <FaCheckCircle className="text-4xl text-purple-600 mx-auto mb-4" />
                <h3 className="text-2xl font-bold text-gray-900 mb-2">Mais conteúdo em breve!</h3>
                <p className="text-gray-600">Estamos adicionando novas listas de vocabulário regularmente</p>
            </div>
        </div>
    )
}

export default VocabularySection



